import {
  TemplateUsedParams,
  useTemplatesUsed,
  useTemplateTask,
  useTemplateTasks,
} from "employee_tasks/hooks/useTasks";
import type { Task } from "@/employee_tasks/types/task";
import React, { useMemo, useState } from "react";
import { Button, Select, Spin, Tooltip } from "antd";
import { PlusOutlined, WarningOutlined } from "@ant-design/icons";

const useTranslation = () => {
  return { t: (text: any) => text };
};
export const TemplateSelect: React.FC<
  {
    record?: Task | Partial<Task>;
    usedTemplatesFilter?: TemplateUsedParams[];
    createTemplateProps?: {
      setIsTemplateModalVisible: (currentSearch: string) => void;
      allowCreate: boolean;
    };
  } & Record<string, any>
> = ({
  record,
  createTemplateProps = undefined,
  usedTemplatesFilter = undefined,
  ...selectProps
}) => {
  const { t } = useTranslation();
  // region template
  const [templateSearch, setTemplateSearch] = useState("");
  const { data: selectedTemplate } = useTemplateTask(selectProps?.value);
  const results = useTemplatesUsed(usedTemplatesFilter || []);
  const usedTemplates = new Set(
    (results || []).filter((item) => !item?.[1]).map((item) => item?.[0]),
  );
  const ignoredTemplates = new Set(
    (results || []).filter((item) => item?.[1]).map((item) => item?.[0]),
  );

  const currentTemplate = record?.template_task || selectedTemplate;

  const {
    data: templates,
    isFetching,
    error,
    processedSearch,
    isSearching,
  } = useTemplateTasks(
    templateSearch,
    {},
    {
      minCharacters: 0,
    },
  );
  // endregion
  const notFoundContent = useMemo(() => {
    if (isFetching && !!templates) {
      return (
        <div style={{ textAlign: "center", padding: "12px" }}>
          <Spin size="small" />
          <div style={{ marginTop: "8px", color: "#999" }}>
            {t("Buscando templates...")}
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ color: "#ff4d4f", padding: "12px", textAlign: "center" }}>
          {t("Erro ao buscar templates")}
        </div>
      );
    }

    if (processedSearch && !isSearching && templates?.length === 0) {
      return (
        <div style={{ padding: "12px" }}>
          <div
            style={{ color: "#999", textAlign: "center", marginBottom: "8px" }}
          >
            {t("Nenhum template encontrado")}
          </div>
          {createTemplateProps?.allowCreate && (
            <Button
              type="dashed"
              block
              icon={<PlusOutlined />}
              onClick={() =>
                createTemplateProps?.setIsTemplateModalVisible(templateSearch)
              }
            >
              {t("Criar novo template")} "{templateSearch}"
            </Button>
          )}
        </div>
      );
    }

    return null;
  }, [
    createTemplateProps,
    error,
    isFetching,
    isSearching,
    processedSearch,
    t,
    templateSearch,
    templates,
  ]);
  const getStatus = (id: string) => {
    if (ignoredTemplates.has(id)) return <span> (Oculto)</span>;
    if (usedTemplates.has(id)) return <span> (Em uso)</span>;
    return null;
  };

  const getTooltipTitle = (id: string) => {
    if (ignoredTemplates.has(id)) return t("Este template foi ocultado");
    if (usedTemplates.has(id))
      return t("Este template já está sendo utilizado");
    return undefined;
  };

  const suffixIcon = useMemo(() => {
    if (isFetching) return undefined;
    if (error)
      return (
        <Tooltip title={String(error)} trigger="hover" placement="bottom">
          <WarningOutlined color="red" />
        </Tooltip>
      );
    return undefined;
  }, [error, isFetching]);
  return (
    <Select
      placeholder={t("Selecione ou busque por template")}
      showSearch
      notFoundContent={notFoundContent}
      allowClear={!error}
      showAction={["focus", "click"]}
      onSearch={setTemplateSearch}
      loading={isFetching}
      filterOption={(input: any, option: any) =>
        option?.content.toLowerCase().includes(input.toLowerCase())
      }
      status={error ? "error" : undefined}
      suffixIcon={suffixIcon}
      options={(currentTemplate
        ? ([
            // ensures that the selected template already exists in the search results
            {
              key: currentTemplate?.id,
              value: currentTemplate?.id,
              label: currentTemplate?.name,
              content: currentTemplate?.name,
            },
          ] as any)
        : []
      ).concat(
        (
          (templates as NonNullable<
            ReturnType<typeof useTemplateTasks>["data"]
          >) || []
        )
          .map((template) => ({
            key: template.id,
            value: template.id,
            content: template.name,
            label: (
              <Tooltip placement="right" title={getTooltipTitle(template.id)}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <span>{template.name}</span>
                  {getStatus(template.id)}
                </div>
              </Tooltip>
            ),
            disabled:
              usedTemplates.has(template.id) ||
              ignoredTemplates.has(template.id),
          }))
          // remove duplate template if selected one already exist in the search result
          .filter((t) => t.key !== currentTemplate?.id),
      )}
      {...selectProps}
    />
  );
};
